import { DateTime } from "luxon"
import { Box, Chip, Divider } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { FilterSearch } from "../../../../../../../shared/components/createOrder/components/FilterSearch"

// Hidden for now (Task 260) — restore the Filter button with these:
// import { IconButton } from "@mui/material"
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import { faFilter } from "@fortawesome/pro-light-svg-icons"

export type ProductionFilterChip = Readonly<{
    key: string
    label: string
    onRemove: () => void
}>

const useStyles = makeStyles()((theme) => ({
    card: {
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: "1rem",
        padding: "1.25rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    topRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        flexWrap: "wrap",
    },
    leftGroup: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        flexWrap: "wrap",
    },
    datePill: {
        display: "flex",
        alignItems: "center",
        width: "18rem",
        height: "3.75rem",
        borderRadius: "5.625rem",
        padding: ".25rem",
        background: theme.palette.tailwind.slate[100],
        boxSizing: "border-box",
        position: "relative",
        cursor: "pointer",
    },
    dateHighlight: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        height: "3.25rem",
        background: theme.palette.tailwind.blue[200],
        color: theme.palette.tailwind.blue[900],
        fontWeight: 700,
        fontSize: "1.5rem",
        lineHeight: 1.5,
        letterSpacing: ".009rem",
        borderRadius: "5.5rem",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    calendarBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "4rem",
        flexShrink: 0,
    },
    calendarButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "3rem",
        height: "3rem",
        borderRadius: "5.625rem",
        flexShrink: 0,
        color: theme.palette.tailwind.slate[700],
    },
    calendarIcon: {
        width: "1.125rem",
        height: "1.125rem",
        fontSize: "1.125rem",
    },
    hiddenPickerField: {
        display: "none",
    },
    datePicker: {
        "& .MuiInputBase-input": {
            fontSize: "1.25rem",
            fontWeight: 700,
            padding: ".75rem 1rem",
        },
    },
    actions: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        marginLeft: "auto",
    },
    filterButton: {
        width: "3rem",
        height: "3rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[700],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    searchWrap: {
        "& .MuiOutlinedInput-root.MuiOutlinedInput-root": {
            height: "3rem",
            boxShadow: "none",
            paddingLeft: ".875rem",
            paddingRight: ".875rem",
        },
        "& .MuiInputAdornment-positionStart": {
            marginRight: 0,
        },
        "& .MuiSvgIcon-root": {
            fontSize: "1.25rem",
        },
    },
    divider: {
        borderColor: theme.palette.tailwind.slate[200],
    },
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: ".75rem",
    },
    chip: {
        borderRadius: ".75rem",
        height: "2.5rem",
        padding: "0 1rem",
        background: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.tailwind.slate[900],
        },
        "& .MuiChip-label": {
            paddingLeft: ".5rem",
            fontSize: ".875rem",
        },
        "& .MuiChip-deleteIcon": {
            fontSize: "1.25rem",
            marginRight: 0,
            transition: "all ease-out 0.3s",
            color: theme.palette.tailwind.slate[300],
            "&:hover": {
                color: theme.palette.tailwind.slate[200],
            },
        },
    },
}))

type Props = Readonly<{
    selectedDate: DateTime
    onDateChange: (value: DateTime) => void
    onOpenFilters: () => void
    search: string
    onSearchChange: (value: string) => void
    filterChips: ReadonlyArray<ProductionFilterChip>
    hideDatePicker: boolean
}>

export function ProductionViewToolbar({
    selectedDate,
    onDateChange,
    onOpenFilters,
    search,
    onSearchChange,
    filterChips,
    hideDatePicker,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box className={classes.card}>
            <Box className={classes.topRow}>
                {!hideDatePicker && (
                    <Box className={classes.leftGroup}>
                        <DatePicker
                            value={selectedDate}
                            onChange={(value) => {
                                if (value) onDateChange(value)
                            }}
                            slotProps={{
                                textField: {
                                    className: classes.datePicker,
                                    inputProps: {
                                        "aria-label": l10n.getString("meeting-service-production-view-date-button"),
                                    },
                                },
                            }}
                        />
                    </Box>
                )}

                <Box className={classes.actions}>
                    {/* Hidden for now (Task 260) — Filter button (needs l10n + IconButton/FontAwesomeIcon/faFilter):
                    <IconButton
                        className={classes.filterButton}
                        onClick={onOpenFilters}
                        aria-label={l10n.getString("meeting-service-production-view-filter-button")}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                    </IconButton> */}
                    <Box className={classes.searchWrap}>
                        <FilterSearch
                            search={search}
                            setSearch={onSearchChange}
                            activeWidth="18rem"
                            inactiveWidth="3rem"
                        />
                    </Box>
                </Box>
            </Box>

            {filterChips.length > 0 && (
                <>
                    <Divider />
                    <Box className={classes.chips}>
                        {filterChips.map((chip) => (
                            <Chip key={chip.key} label={chip.label} onDelete={chip.onRemove} className={classes.chip} />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    )
}
